import type {MouseEvent, ReactElement} from 'react';
import React, {useState} from 'react';
import type {RecommendedTemplate, TemplatesForInspirationTileProps} from './templates-for-inspiration-tile.types';
import {MIN_TEMPLATES_TO_RENDER, TEMPLATES_THUMBNAIL_COUNT_FULL, TEMPLATES_THUMBNAIL_COUNT_HALF, TemplatesForInspirationTileVariant} from './templates-for-inspiration-tile.types';
import styles from './templates-for-inspiration-tile.module.scss';
import {Button, Size, Type} from '@Components/button';
import {Text} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {getAiChatPromptBoxId, openAttachTemplateGalleryDialog} from '@Libraries/ai-chat.library';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getAssetUrl} from '@Utils/s3.util';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';

export function TemplatesForInspirationTile({chatId, hashedId: _hashedId, variant, showCollapsed}: TemplatesForInspirationTileProps): ReactElement | null {
  const {state} = useAiChat();
  const {actions: promptBoxActions} = useAiPromptBox(getAiChatPromptBoxId(chatId));
  const [initiallyCollapsed] = useState<boolean>(showCollapsed);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(initiallyCollapsed);
  const [failedTemplateIds, setFailedTemplateIds] = useState<Set<string>>(new Set());
  const thumbnailCount = variant === TemplatesForInspirationTileVariant.HALF ? TEMPLATES_THUMBNAIL_COUNT_HALF : TEMPLATES_THUMBNAIL_COUNT_FULL;
  const variantClass = variant === TemplatesForInspirationTileVariant.HALF ? styles.half : styles.full;
  const displayTemplates = state
    .getRecommendedTemplates(chatId)
    .filter((template) => !failedTemplateIds.has(template.hashedId))
    .slice(0, thumbnailCount);

  if (displayTemplates.length < MIN_TEMPLATES_TO_RENDER) {
    return null;
  }

  const handleViewMore = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_VIEWMORE);
    openAttachTemplateGalleryDialog(state.getTemplateSearchTerm(chatId) ?? undefined);
  };

  const handleTemplateClick = (template: RecommendedTemplate): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_ATTACH, {[GA4EventParamName.TYPE]: 'chat'});
    promptBoxActions.addAttachments([
      {
        type: PromptAttachmentType.DESIGN_TEMPLATE,
        designId: template.hashedId,
        previewURL: template.previewUrl,
        pageHashedId: template.pageHashedId,
      },
    ]);
  };

  const handleThumbnailLoadError = (hashedId: string): void => {
    setFailedTemplateIds((prev) => {
      if (prev.has(hashedId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(hashedId);
      return next;
    });
  };

  const handleToggleCollapsed = (e: MouseEvent): void => {
    e.stopPropagation();
    setIsCollapsed((prev) => !prev);
  };

  const handleExpandFromCard = (): void => {
    setIsCollapsed(false);
  };

  const getCaretToggle = (): ReactElement | null => {
    if (!initiallyCollapsed) {
      return null;
    }

    return <Button type={Type.GHOST} size={Size.XSMALL} icon={isCollapsed ? 'icon-caret-down' : 'icon-caret-up'} onClick={handleToggleCollapsed} customClasses={styles.caretButton} />;
  };

  const getViewMoreButton = (): ReactElement => {
    return (
      <div className={styles.viewMoreWrapper}>
        <Button type={Type.SECONDARY} size={Size.XSMALL} text={window.i18next.t('pmwjs_view_more')} icon={'icon-designs'} onClick={handleViewMore} customClasses={'radius-8'} />
      </div>
    );
  };

  const getHeaderDescription = (): ReactElement => {
    return (
      <div className={styles.headerText}>
        <Text className={styles.title} val={window.i18next.t('pmwjs_want_to_try_another_look')} />
        <div className={styles.subtitleRow}>
          <Text className={styles.subtitle} val={window.i18next.t('pmwjs_select_a_template_to_redesign_it')} />
          <Icon icon={'icon-mouse-click'} size={IconSize.SIZE_ICON_16} />
        </div>
      </div>
    );
  };

  const getHeaderActions = (): ReactElement => {
    return (
      <div className={styles.headerActions}>
        {getViewMoreButton()}
        {getCaretToggle()}
      </div>
    );
  };

  const getHeader = (): ReactElement => {
    return (
      <div className={styles.header}>
        {getHeaderDescription()}
        {getHeaderActions()}
      </div>
    );
  };

  const getThumbnails = (): ReactElement => {
    return (
      <div className={styles.thumbnailsRow}>
        {displayTemplates.map((template) => (
          <ClickableDiv
            key={template.hashedId}
            className={styles.thumbnail}
            onClick={(): void => {
              handleTemplateClick(template);
            }}
          >
            <img
              className={styles.thumbnailImage}
              src={template.previewUrl}
              alt=""
              onError={(): void => {
                handleThumbnailLoadError(template.hashedId);
              }}
            />
            <div className={styles.hoverOverlay}>
              <Icon icon={'icon-paperclip'} size={IconSize.SIZE_ICON_16} type={IconType.PREMIUM} className={styles.hoverIcon} />
              <Text className={styles.hoverText} val={window.i18next.t('pmwjs_attach')} />
            </div>
          </ClickableDiv>
        ))}
      </div>
    );
  };

  const getCollapsedTemplatesImage = (): ReactElement | null => {
    if (!initiallyCollapsed) {
      return null;
    }
    return <img className={styles.collapsedTemplatesImage} src={getAssetUrl('ai-chat-templates-for-inspiration-mobile.webp')} alt="" aria-hidden="true" />;
  };

  const getMobileTile = (): ReactElement => {
    return (
      <ClickableDiv className={styles.mobileTileCard} onClick={handleViewMore}>
        <div className={styles.mobileTextContent}>
          <Text className={styles.title} val={window.i18next.t('pmwjs_want_to_try_another_look')} />
          <Text className={styles.subtitle} val={window.i18next.t('pmwjs_redesign_a_template_with_ai')} />
        </div>
        <img className={styles.mobileTileImage} src={getAssetUrl('ai-chat-templates-for-inspiration-mobile.webp')} alt="" />
      </ClickableDiv>
    );
  };

  const getDesktopTile = (): ReactElement => {
    const cardClasses = `${styles.tileCard} ${variantClass} ${isCollapsed ? styles.collapsed : ''}`;
    return (
      <ClickableDiv className={cardClasses} onClick={handleExpandFromCard} role={isCollapsed ? 'button' : 'presentation'} tabIndex={isCollapsed ? 0 : -1}>
        {getHeader()}
        {getThumbnails()}
        {getCollapsedTemplatesImage()}
      </ClickableDiv>
    );
  };

  const getTile = (): ReactElement => {
    if (state.isSmall) {
      return getMobileTile();
    }
    return getDesktopTile();
  };

  return getTile();
}
